/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2026.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import ExpandedGridRowsContext from "qqq/components/query/ExpandedGridRowsContext";
import React, {useContext} from "react";

interface Props
{
   title: JSX.Element;
   field: string;
   separator: string;
}


/*******************************************************************************
 ** The header of a multi-line column: the column title, plus the same control
 ** its cells carry, here expanding or collapsing every row that has a multi-entry
 ** value in this column.  It shows the collapse form only when every such row is
 ** already expanded.  Without an ExpandedGridRows provider (a grid that cannot
 ** grow its rows), or with no expandable row, there is no control, just the title.
 *******************************************************************************/
export default function MultiLineColumnHeader({title, field, separator}: Props): JSX.Element
{
   const expandedGridRows = useContext(ExpandedGridRowsContext);

   ////////////////////////////////////////////////////////////////////////////////////
   // only rows with more than one entry in this column; rows with nothing to expand //
   // are left alone, so their other cells do not grow                               //
   ////////////////////////////////////////////////////////////////////////////////////
   const expandableRowIds = expandedGridRows.isProvided
      ? expandedGridRows.rows.filter((row) => typeof row.values?.[field] === "string" && row.values[field].includes(separator)).map((row) => row.id)
      : [];

   if (expandableRowIds.length == 0)
   {
      return (title);
   }

   const allExpanded = expandableRowIds.every((rowId) => expandedGridRows.expandedRowIds.has(rowId));

   /////////////////////////////////////////////////////////////////////////////////
   // swallow mouse events so the click never reaches the header (sorting, menus) //
   /////////////////////////////////////////////////////////////////////////////////
   const swallow = (event: React.MouseEvent) =>
   {
      event.stopPropagation();
   };

   const toggleAll = (event: React.MouseEvent) =>
   {
      event.stopPropagation();
      event.preventDefault();
      expandedGridRows.setRowsExpanded(expandableRowIds, !allExpanded);
   };

   /////////////////////////////////////////////////////////////////////////////////////
   // anchored near the header's right edge, left of where the hover column menu (the //
   // three dots) appears, so the two never overlap; the cells' controls carry the    //
   // same offset so they line up under it, and the title container leaves room (css) //
   /////////////////////////////////////////////////////////////////////////////////////
   const controlLabel = allExpanded ? "Show all on one line" : "Show all one per line";

   return (
      <>
         {title}
         <IconButton size="small" onClick={toggleAll} onMouseDown={swallow} onMouseUp={swallow} onDoubleClick={swallow} aria-label={controlLabel} title={controlLabel} sx={{position: "absolute", right: "34px", top: "50%", transform: "translateY(-50%)", p: "4px"}}>
            <Icon fontSize="small">{allExpanded ? "unfold_less" : "unfold_more"}</Icon>
         </IconButton>
      </>
   );
}
